import React, { useState } from 'react';
import { connectionsAPI } from '../../services/api';

const describeError = (err, fallback) => err?.response?.data?.error || err?.message || fallback;
const intervalLabel = (minutes) => {
  if (minutes === 1440) return 'Daily';
  if (minutes >= 60) return `Every ${minutes / 60} hour${minutes === 60 ? '' : 's'}`;
  return `Every ${minutes} minutes`;
};

/** For an accounting source (QuickBooks, Xero): how often to sync, how much history to read, and whether the Profit and Loss is on an accrual or cash basis. */
export default function QuickBooksSettings({ connector, connection, companyId, onSaved }) {
  const s = connection.definitions || {};
  const [draft, setDraft] = useState({
    interval: connection.syncIntervalMinutes,
    historyMonths: s.historyMonths ?? 24,
    accountingMethod: s.accountingMethod || 'Accrual',
  });
  const [error, setError] = useState('');
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);

  const set = (patch) => {
    setSaved(false);
    setDraft((d) => ({ ...d, ...patch }));
  };

  const save = async (e) => {
    e.preventDefault();
    try {
      setSaving(true);
      setError('');
      const res = await connectionsAPI.saveSettings(connector.id, companyId, {
        syncIntervalMinutes: draft.interval,
        settings: { historyMonths: Number(draft.historyMonths), accountingMethod: draft.accountingMethod },
      });
      setSaved(true);
      onSaved(res.data);
    } catch (err) {
      setError(describeError(err, 'Could not save the settings'));
    } finally {
      setSaving(false);
    }
  };

  return (
    <form className="connection-settings" onSubmit={save}>
      <label className="connection-field">
        <span>Sync frequency</span>
        <select value={draft.interval} onChange={(e) => set({ interval: Number(e.target.value) })}>
          {connector.syncIntervals.map((m) => <option key={m} value={m}>{intervalLabel(m)}</option>)}
        </select>
      </label>
      <p className="source-hint">Each sync counts towards Kinspeed&apos;s {connector.title} usage, shared by every company, so accounts rarely need syncing more than every few hours.</p>

      <label className="connection-field">
        <span>Accounting method</span>
        <select value={draft.accountingMethod} onChange={(e) => set({ accountingMethod: e.target.value })}>
          <option value="Accrual">Accrual: income and costs when invoiced and billed</option>
          <option value="Cash">Cash: income and costs when paid</option>
        </select>
      </label>

      <label className="connection-field">
        <span>Months of history</span>
        <input type="number" min="1" max="120" step="1" value={draft.historyMonths} onChange={(e) => set({ historyMonths: e.target.value })} />
      </label>
      <p className="source-hint">Unpaid invoices and bills are always read, however old.</p>

      {error && <div className="error-message" role="alert">{error}</div>}
      <div className="connection-actions">
        <button type="submit" className="btn-primary" disabled={saving}>{saving ? 'Saving…' : 'Save settings'}</button>
        {saved && <span className="source-hint connection-saved">Saved. The next sync uses these settings.</span>}
      </div>
    </form>
  );
}
